<template>
  <ModalOverlay
    v-model="showModal"
    header-background-class="b-pop-up__header"
    background-class="b-pop-up--alert-success b-pop-up__holder"
    no-close-backdrop
    @close="onCloseModal">
    <template #header>
      <div class="b-pop-up__header__title">{{ item.name }}</div>
    </template>
    <template #body>
      <div class="b-pop-up__content">
        <Loader v-if="isLoading"></Loader>
        <Alert
          v-if="errorMsgs.length"
          type="danger"
          :messages="errorMsgs"></Alert>

        <div v-if="dictionary.modalCD4UTitle" class="p-booking__modal__title">
          {{ dictionary.modalCD4UTitle }}
        </div>
        <div
          v-if="dictionary.modalCD4UDescription"
          class="p-booking__modal__desc">
          {{ dictionary.modalCD4UDescription }}
        </div>
        <div class="p-booking__modal__voucher">
          <div>{{ dictionary.codesNumbers }}</div>
          <div class="b-ticket__counter-wrapper">
            <!-- remove -->
            <div>
              <button
                name="remove"
                class="btn-icon btn--minus-icon"
                type="button"
                :disabled="isDisabledRemove"
                @click="onClickRemove"></button>
            </div>
            <div class="b-ticket__counter">
              {{ codes.length }}
            </div>
            <div>
              <!-- add -->
              <button
                name="add"
                type="button"
                class="btn-icon btn-plus-icon"
                :disabled="isDisabledAdd"
                @click="onClickAdd"></button>
            </div>
          </div>
        </div>

        <form autocomplete="off" @submit.prevent="onSubmitForm">
          <div class="p-booking__modal__vouchers-wrapper">
            <div
              v-for="(entry, index) in codes"
              :key="index"
              class="p-booking__modal__input-wrapper">
              <div class="code-input-group">
                <div class="form-floating">
                  <input
                    :id="item.id + index"
                    v-model.trim="codes[index].name"
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.voucherCode"
                    :maxlength="lengthCD4UCode"
                    :class="{
                      'form-control--error':
                        (v$.codes.$error &&
                          v$.codes.$each.$response.$data[index].name
                            .$invalid) ||
                        codes[index].message,
                    }"
                    @blur="v$.codes.$touch" />

                  <label :for="item.id + index">
                    {{ dictionary.voucherCode }}
                  </label>
                </div>

                <i
                  v-if="codes.length > 1"
                  class="icon-close"
                  :title="dictionary.codeCD4URemove"
                  @click="onClickDelete(index)"></i>
              </div>

              <div v-if="codes[index].message" class="field-validation-error">
                {{ codes[index].message }}
              </div>

              <!-- validations -->
              <template v-if="v$.codes.$error">
                <template
                  v-if="v$.codes.$each.$response.$data[index].name.$invalid">
                  <div
                    v-if="!v$.codes.$each.$response.$data[index].name.required"
                    class="field-validation-error">
                    {{ dictionary.codeCD4URequired }}
                  </div>
                  <div
                    v-if="!v$.codes.$each.$response.$data[index].name.numeric"
                    class="field-validation-error">
                    {{ dictionary.codeCD4UNumeric }}
                  </div>
                  <div
                    v-if="!v$.codes.$each.$response.$data[index].name.minLength"
                    class="field-validation-error">
                    {{ dictionary.codeCD4ULength }}
                  </div>
                  <div
                    v-if="!v$.codes.$each.$response.$data[index].name.maxLength"
                    class="field-validation-error">
                    {{ dictionary.codeCD4ULength }}
                  </div>
                </template>
              </template>
            </div>
          </div>
          <button
            type="submit"
            class="p-booking__modal__cta btn-primary-orange"
            :disabled="isLoading">
            <span>{{ dictionary.registerCodes }}</span>
          </button>
        </form>
      </div>
    </template>
  </ModalOverlay>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'
import Loader from './../../../components/Shared/Loader.vue'
import ModalOverlay from './../../../components/ModalOverlay.vue'
import Alert from './../../../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import {
  helpers,
  maxLength,
  minLength,
  numeric,
  required,
} from '@vuelidate/validators'
import getErrorMessage from './../../../../lib/getErrorMessage'
import { useBookingStore } from './../../../store/BookingStore'
import axios from 'axios'
import * as lib from './../../../../lib/lib'

const { configuration, dictionary } =
  getCurrentInstance().appContext.config.globalProperties
const { lengthCD4UCode, maxCD4UCodes } = configuration
const emit = defineEmits(['close-modal'])
const codes = ref([{ name: '' }])
const showModal = ref(false)
const isLoading = ref(false)
const errorMsgs = ref([])
const store = useBookingStore()

const rules = computed(() => ({
  codes: {
    $each: helpers.forEach({
      name: {
        required,
        minLength: minLength(lengthCD4UCode),
        maxLength: maxLength(lengthCD4UCode),
        numeric,
      },
    }),
  },
}))

const v$ = useVuelidate(rules, { codes })

// props
const props = defineProps({
  showModalProp: {
    default() {
      return false
    },
    type: Boolean,
    required: true,
  },
  item: {
    default() {
      return null
    },
    type: Object,
    required: true,
  },
  arrayIndex: {
    type: Number,
    required: true,
  },
})

watch(
  () => props.showModalProp,
  (newVal) => {
    showModal.value = newVal
  }
)

const isDisabledRemove = computed(() => {
  return codes.value.length === 1
})

const count = computed(() => {
  const { selectedTickets } = store.bookingProgress
  const entry = selectedTickets[props.item.id]
  return entry ? entry.count : 0
})

const isDisabledAdd = computed(() => {
  const sum = store.getSeatsCount + codes.value.length

  if (sum === configuration.maximumTickets) {
    return true
  }

  const { length } = codes.value

  return length + count.value === maxCD4UCodes
})

const onClickAdd = () => {
  codes.value.push({ name: '' })
  resetValidation()
}

const onClickRemove = () => {
  codes.value.pop()
}

const onClickDelete = (index) => {
  codes.value.splice(index, 1)
}

const onSubmitForm = async () => {
  v$.value.$validate()

  if (v$.value.$invalid) {
    await nextTick()
    return false
  }

  removeErrors()
  isLoading.value = true

  const endpoint = configuration.endpointCD4UValidateCode
  try {
    const offerId = Number(props.item.offerId)
    if (isNaN(offerId)) {
      console.error('Offer ID is not a number')
      return handleError([dictionary.defaultErrorMessage])
    }
    const resp = await axios.post(
      endpoint,
      {
        ticketId: props.item.id,
        offerId,
        codes: codes.value.map((i) => i.name),
        language: document.documentElement.lang,
        userSessionId: store.bookingProgress.usersessionId,
      },
      lib.axiosConfig
    )
    const data = resp.data

    if (data.hasError) {
      return handleError([dictionary.defaultErrorMessage])
    }

    data.data.forEach((element, index) => {
      const { isValid, message } = element

      if (!isValid) {
        codes.value[index].message = message
      }
    })

    const ticket = {
      ticketId: props.item.id,
      ticketPrice: props.item.ticketPrice,
      ticketDescription: props.item.name,
      offerId: props.item.offerId,
      seatsCount: props.item.seatsCount,
      points:props.item.points
    }

    // all codes are valid
    if (data.data.every((i) => i.isValid)) {
      data.data.forEach((element, index) => {
        const { code } = element
        const entry = Object.assign(ticket, { code })

        store.addTicket(entry)
        codes.value[index].message = null
      })
      handleSuccess()
    }

    isLoading.value = false
  } catch (errors) {
    handleError([dictionary.defaultErrorMessage])
    console.error(errors)
  }
}

const handleSuccess = () => {
  isLoading.value = false
  removeErrors()
  onCloseModal()
}

const handleError = async (messages) => {
  isLoading.value = false
  errorMsgs.value = getErrorMessage(messages, dictionary.defaultErrorMessage)
}

const removeErrors = () => {
  errorMsgs.value = []
}

const onCloseModal = () => {
  emit('close-modal', props.arrayIndex)
  resetCodes()
  resetValidation()
  removeErrors()
}

const resetCodes = () => {
  codes.value = [{ name: '' }]
}

const resetValidation = () => {
  v$.value.$reset()
}
</script>
